import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Lock, Radio, Zap, Eye, Clock, CheckCircle, Building2, AlertTriangle, Wifi, Server, Fingerprint, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import QuoteForm from '../components/QuoteForm';

const SuperiorAlarmG3Page = () => {
  // Products G3 with Ajax official BLACK images
  const g3Products = [
    {
      name: "Hub G3",
      fullName: "Superior Hub G3 Jeweller",
      description: "Centrale sans fil Grade 3",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_hub_g3_black_206d95745c%402.png",
      specs: "250 appareils • 4G/Ethernet/Wi-Fi"
    },
    {
      name: "Hub Hybrid",
      fullName: "Superior Hub Hybrid (4G)",
      description: "Centrale hybride Fibra + Jeweller",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fhub_hybrid_black_eb78efaa15%402.png",
      specs: "200 appareils • Grade 2"
    },
    {
      name: "Hub Hybrid 2",
      fullName: "Superior Hub Hybrid 2",
      description: "Centrale hybride évoluée",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_hub_hybrid_2_black_52f36d51a5%402.png",
      specs: "250 appareils • 64 scénarios"
    },
    {
      name: "MegaHub",
      fullName: "Superior MegaHub",
      description: "Centrale pour grands projets",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fmega_hub_9b1d9a8dce%402.png",
      specs: "999 appareils • 1000 users"
    },
    {
      name: "DoorProtect G3",
      fullName: "Superior DoorProtect G3 Jeweller",
      description: "Détecteur d'ouverture",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_doorprotect_g3_jeweller_black_e171384a81%402.png",
      specs: "Chocs • Inclinaison • Anti-masquage"
    },
    {
      name: "MotionProtect G3",
      fullName: "Superior MotionProtect G3 Jeweller",
      description: "Détecteur PIR anti-masquage",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_motionprotect_g3_jeweller_black_9dd3c54d27%402.png",
      specs: "Portée 15m • Anti-masquage"
    },
    {
      name: "MotionProtect Plus G3",
      fullName: "Superior MotionProtect Plus G3 Jeweller",
      description: "Détecteur PIR + Micro-ondes",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_motionprotect_plus_g3_black_ddb9c607bf%402.png",
      specs: "Double techno • Zéro fausse alarme"
    },
    {
      name: "KeyPad Plus G3",
      fullName: "Superior KeyPad Plus G3 Jeweller",
      description: "Clavier tactile sans fil",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_keypad_plus_g3_jeweller_black_61a92301a5%402.png",
      specs: "DESFire® • Pass/Tag"
    },
    {
      name: "KeyPad TouchScreen G3",
      fullName: "Superior KeyPad TouchScreen G3",
      description: "Clavier écran tactile 5\"",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_keypad_ts_g3_white_e23b6bdc8b%402.png",
      specs: "Écran 5\" • BLE • DESFire®"
    },
    {
      name: "HomeSiren G3",
      fullName: "Superior HomeSiren G3 Jeweller",
      description: "Sirène intérieure",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_home_siren_g_3_black_595f314ab3%402.png",
      specs: "105 dB • LED"
    },
    {
      name: "StreetSiren Plus G3",
      fullName: "Superior StreetSiren Plus G3",
      description: "Sirène extérieure",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_streetsiren_plus_g3_black_c992fc6f10%402.png",
      specs: "113 dB • IP55"
    },
    {
      name: "DoubleButton G3",
      fullName: "Superior DoubleButton G3 Jeweller",
      description: "Bouton de panique",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fdoublebutton_3b06f09e9d%402.png",
      specs: "Double pression • IP55"
    },
    {
      name: "MultiTransmitter G3",
      fullName: "Superior MultiTransmitter G3",
      description: "Module d'intégration",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_multitransmitter_g3_black_b1cb7475c2%402.png",
      specs: "18 zones • NC/NO/EOL"
    },
  ];

  const antiSabotageFeatures = [
    {
      title: "Détection de brouillage",
      description: "Bascule automatique entre 17 fréquences sur 4 bandes",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fjamming_g3_020acd6dc6%401.jpg"
    },
    {
      title: "Anti-masquage actif",
      description: "Détecte tissu, papier, métal ou aérosols",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fadvanced_antimasking_c0a02c2f8c%402.jpg"
    },
    {
      title: "Autonomie en cas de coupure",
      description: "Basculement instantané sur batteries de secours",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fautonomous_operation_580c666a64%401.jpg"
    },
    {
      title: "Chiffrement AES-128",
      description: "Données chiffrées, authentification individuelle",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fspoofing_ae6f6bec06%402.jpg"
    },
    {
      title: "Perte de communication",
      description: "Détection en 36 secondes seulement",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsommunication_failure_detection_62e9722f90%402.jpg"
    },
    {
      title: "Bouton anti-sabotage",
      description: "Alarme si démontage ou retrait du dispositif",
      image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Ftamper_612afb2ec3%402.jpg"
    },
  ];

  const targetSites = [
    { name: "Site de production", image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Ffactory_sm_b89c413e62%402.jpg" },
    { name: "Musée", image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fmuseum_sm_424ab907c2%402.jpg" },
    { name: "Station essence", image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fgas_station_sm_d99f5390a9%402.jpg" },
    { name: "Banque", image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fbank_sm_5858a09233%402.jpg" },
    { name: "Joaillerie", image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fjewellery_store_sm_09d3ae3ada%402.jpg" },
    { name: "Armurerie", image: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Farmory_sm_421a0875d8%402.jpg" },
  ];

  const keyFeatures = [
    { icon: Radio, title: "100% Sans fil", desc: "Un câble d'alimentation et Ethernet suffisent" },
    { icon: Clock, title: "Installation 2 min", desc: "Par dispositif, zéro câblage" },
    { icon: Zap, title: "Alarme < 1 seconde", desc: "Transmission instantanée au centre" },
    { icon: Eye, title: "Perte détectée 36s", desc: "Supervision continue" },
    { icon: Lock, title: "250 dispositifs", desc: "Une seule centrale" },
    { icon: Shield, title: "Certifié EN 50131", desc: "Grade 3" },
  ];

  return (
    <div className="min-h-screen py-12 bg-black" data-testid="superior-alarm-g3-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Alarme Grade 3 Sites Sensibles | Ajax Fibra Pro - FGS Alarme Montpellier</title>
        <meta name="description" content="Alarme filaire Grade 3 pour sites sensibles : banques, bijouteries, musées. Ajax Fibra haute sécurité, certification EN 50131. Installation pro Montpellier. Garantie 5 ans." />
        <meta name="keywords" content="alarme grade 3, alarme filaire, ajax fibra, site sensible, banque, bijouterie, armurerie, alarme haute sécurité, système filaire pro, télésurveillance, certification en 50131" />
        <link rel="canonical" href="https://maprotection-ia.fr/superior-alarm-g3" />
      </Helmet>

      {/* Hero */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12">
        <div className="relative rounded-2xl md:rounded-3xl overflow-hidden">
          <img 
            src="https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fwireless_g3_xl_68b8679e21%402.jpg" 
            alt="Solution sans fil Grade 3"
            className="w-full h-[350px] md:h-[400px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent flex items-center">
            <div className="px-6 md:px-12 max-w-2xl">
              <p className="text-amber-400 text-sm font-medium tracking-wide uppercase mb-3">Certifié EN 50131 Grade 3</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4 tracking-tight">
                Solution Sans Fil <span className="text-amber-400">Grade 3</span>
              </h1>
              <p className="text-base md:text-lg text-white/50 mb-6 font-light">
                La première protection anti-intrusion sans fil au monde pour les sites de sécurité avancée
              </p>
              <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-fgs-primary/10 border border-fgs-primary/20 rounded-full">
                  <Shield className="w-4 h-4 text-fgs-primary" />
                  <span className="text-white/70 text-sm">Garantie 5 ans</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full">
                  <Building2 className="w-4 h-4 text-white/50" />
                  <span className="text-white/70 text-sm">Sur devis</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features Carousel */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <FeaturesCarousel items={keyFeatures} color="amber" />
      </section>

      {/* Sécurité maximale section */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4 tracking-tight">
            Sécurité maximale. Désormais sans fil.
          </h2>
          <p className="text-white/60 max-w-3xl mx-auto font-light">
            Les algorithmes Ajax avancés réagissent instantanément aux menaces réelles. 
            Avec une précision de niveau Grade 3 et des protocoles ultra-rapides, les alarmes atteignent 
            les centres de télésurveillance en moins d'une seconde.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Radio className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white">Connectivité sans fil</h3>
            </div>
            <p className="text-fgs-text-muted">
              Un seul câble d'alimentation et un câble Ethernet suffisent. Le reste, c'est la liberté totale : 
              une gamme de dispositifs entièrement sans fil, prête à prendre en charge tous les scénarios.
            </p>
          </div>
          <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Zap className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white">Déploiement le plus rapide</h3>
            </div>
            <p className="text-fgs-text-muted">
              Zéro câble, zéro attente : 2 minutes suffisent pour l'installation de chaque dispositif. 
              Le système est connecté et configuré via des applications Ajax.
            </p>
          </div>
        </div>
      </section>

      {/* Protection contre le sabotage */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-4">
            Protection contre le sabotage Grade 3
          </h2>
          <p className="text-fgs-text-muted max-w-3xl mx-auto">
            Chaque dispositif est doté d'un bouton anti-sabotage qui réagit immédiatement. 
            La centrale surveille en permanence l'état des dispositifs et identifie les pertes de communication en 36 secondes.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {antiSabotageFeatures.map((feature, i) => (
            <div key={i} className="bg-fgs-surface border border-fgs-border rounded-xl overflow-hidden hover:border-amber-500/40 transition-colors group">
              <div className="h-40 overflow-hidden">
                <img 
                  src={feature.image} 
                  alt={feature.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4">
                <h3 className="font-heading text-base font-bold text-white mb-1">{feature.title}</h3>
                <p className="text-sm text-fgs-text-muted">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sites cibles */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
            Protection des sites à haute sécurité
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {targetSites.map((site, i) => (
            <div key={i} className="relative rounded-xl overflow-hidden group cursor-pointer">
              <img 
                src={site.image} 
                alt={site.name}
                className="w-full h-32 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end">
                <span className="text-white text-sm font-medium p-3">{site.name}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Gamme complète de produits G3 - Carousel */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-4">
            Gamme complète Grade 3
          </h2>
          <p className="text-fgs-text-muted max-w-2xl mx-auto">
            Tous les dispositifs certifiés EN 50131 Grade 3 pour une protection sans compromis
          </p>
        </div>

        <ProductsCarousel products={g3Products} color="amber" />
      </section>

      {/* Technologies sans fil */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="bg-gradient-to-r from-amber-900/30 to-fgs-surface border border-amber-500/20 rounded-2xl overflow-hidden">
          <div className="grid lg:grid-cols-2">
            <div className="p-8 md:p-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-6">
                Technologies sans fil uniques
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Wifi className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Superior Jeweller</h4>
                    <p className="text-sm text-fgs-text-muted">Envoie les alertes en seulement 0,15 seconde</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Eye className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">TurboWings</h4>
                    <p className="text-sm text-fgs-text-muted">Vérification photo et transport de données sur longues distances</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Server className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Portée jusqu'à 3500m</h4>
                    <p className="text-sm text-fgs-text-muted">En champ ouvert avec chiffrement robuste</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-fgs-bg/50 p-8 md:p-10">
              <img 
                src="https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fsuperior_xxl_d165f6741f%402.jpg"
                alt="Technologies Ajax"
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="bg-fgs-surface border border-fgs-border rounded-xl p-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-white uppercase mb-6">
            Performances testées et certifiées
          </h2>
          <p className="text-fgs-text-muted max-w-3xl mx-auto mb-8">
            Les dispositifs Ajax G3 sont soigneusement testés et certifiés conformes à la norme EN 50131 (Grade 3) 
            par des laboratoires indépendants en Europe.
          </p>
          <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto">
            <div className="flex flex-col items-center">
              <CheckCircle className="w-10 h-10 text-amber-400 mb-2" />
              <span className="text-sm text-white font-medium">Test en conditions réelles</span>
            </div>
            <div className="flex flex-col items-center">
              <Fingerprint className="w-10 h-10 text-amber-400 mb-2" />
              <span className="text-sm text-white font-medium">Communication certifiée</span>
            </div>
            <div className="flex flex-col items-center">
              <AlertTriangle className="w-10 h-10 text-amber-400 mb-2" />
              <span className="text-sm text-white font-medium">Notification des défauts</span>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form - Simplified */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <h2 className="font-heading text-3xl font-bold text-white uppercase mb-4">
              Demandez votre étude Grade 3
            </h2>
            <p className="text-fgs-text-muted mb-6">
              Seuls les partenaires accrédités peuvent installer les produits Ajax Grade 3. 
              FGS est certifié pour vous accompagner dans votre projet de sécurité haute performance.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-fgs-primary flex-shrink-0" />
                <span className="text-white">Audit de sécurité gratuit de votre site</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-fgs-primary flex-shrink-0" />
                <span className="text-white">Dimensionnement personnalisé du système</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-fgs-primary flex-shrink-0" />
                <span className="text-white">Installation par techniciens certifiés Ajax</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-fgs-primary flex-shrink-0" />
                <span className="text-white">Garantie 5 ans FGS incluse</span>
              </div>
            </div>
          </div>
          <div>
            <QuoteForm category="grade3" />
          </div>
        </div>
      </section>
    </div>
  );
};

// Features Carousel Component
const FeaturesCarousel = ({ items, color = 'amber' }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi]);

  const colorClasses = {
    amber: { icon: 'text-amber-400', border: 'hover:border-amber-500/40' },
  };

  return (
    <div className="relative">
      <div className="hidden md:flex absolute -top-2 right-0 gap-2 z-10">
        <button onClick={() => emblaApi?.scrollPrev()} disabled={!canScrollPrev}
          className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-amber-500/50 disabled:opacity-30 transition-all">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <button onClick={() => emblaApi?.scrollNext()} disabled={!canScrollNext}
          className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-amber-500/50 disabled:opacity-30 transition-all">
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {items.map((feature, i) => (
            <div key={i} className="flex-shrink-0 w-[200px] md:w-[220px]">
              <div className={`h-full bg-fgs-surface border border-fgs-border rounded-xl p-5 text-center ${colorClasses[color].border} transition-colors`}>
                <feature.icon className={`w-10 h-10 ${colorClasses[color].icon} mx-auto mb-3`} />
                <h3 className="font-heading text-sm font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-xs text-fgs-text-muted">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:hidden flex items-center justify-center gap-2 mt-4 text-fgs-text-muted text-xs">
        <ChevronLeft className="w-3 h-3" />
        <span>Glissez pour voir plus</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    </div>
  );
};

// Products Carousel Component
const ProductsCarousel = ({ products, color = 'amber' }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi]);

  const colorClasses = {
    amber: { border: 'hover:border-amber-500/40', text: 'text-amber-400' },
  };

  return (
    <div className="relative">
      <div className="hidden md:flex absolute -top-12 right-0 gap-2 z-10">
        <button onClick={() => emblaApi?.scrollPrev()} disabled={!canScrollPrev}
          className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-amber-500/50 disabled:opacity-30 transition-all">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <button onClick={() => emblaApi?.scrollNext()} disabled={!canScrollNext}
          className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-amber-500/50 disabled:opacity-30 transition-all">
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {products.map((product, i) => (
            <div key={i} className="flex-shrink-0 w-[180px] md:w-[200px]">
              <div className={`h-full bg-fgs-surface border border-fgs-border rounded-xl p-4 ${colorClasses[color].border} transition-all duration-300 group`}>
                <div className="bg-white rounded-lg p-3 mb-3 aspect-square flex items-center justify-center">
                  <img src={product.image} alt={product.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                </div>
                <h3 className="font-heading text-sm font-bold text-white mb-1">{product.name}</h3>
                <p className="text-xs text-fgs-text-muted mb-2">{product.description}</p>
                <p className={`text-[11px] ${colorClasses[color].text}`}>{product.specs}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:hidden flex items-center justify-center gap-2 mt-4 text-fgs-text-muted text-xs">
        <ChevronLeft className="w-3 h-3" />
        <span>Glissez pour voir plus</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    </div>
  );
};

export default SuperiorAlarmG3Page;
